<template>
  <div @click="onOrderClicked">
    <v-card
      v-touch:swipe="onSwipe"
      outlined
      class="py-2 px-3 my-md-4 my-lg-4 my-xl-4"
      :style="
        `border-color: ${orderColor()}; border-style: dashed; border-width: 1px`
      "
    >
      <div class="d-flex flex-column">
        <div
          :class="{
            'd-flex': true,
            'justify-space-between': true,
            'align-center': true
          }"
        >
          <div class="mb-2">
            <div class="d-flex justify-space-between align-center">
              <div @click.stop.prevent="onUserClicked">
                <Avatars class="mr-3" height="20px" :seed="order.userId" />
              </div>
              <div class="caption">#{{ order.id }}</div>
            </div>
          </div>
          <div class="mb-3" @click.stop.prevent="maybeTogglePayment">
            <v-icon :color="order.hasBeenPaid ? 'primary' : null">
              {{ order.hasBeenPaid ? "mdi-cash" : "mdi-cash-remove" }}
            </v-icon>
          </div>
        </div>
        <div class="d-flex justify-space-between align-center">
          <div @click.stop.prevent="onLocationClicked" class="body-2">
            {{ location.name }}
          </div>

          <div class="d-flex align-center" :style="`color: ${time.color}`">
            <v-icon dense class="mr-1" :color="time.color">
              mdi-clock-outline
            </v-icon>
            <div>{{ time.waited }}</div>
          </div>
        </div>
      </div>
      <div :class="{ 'd-flex': true, 'flex-column': true }">
        <div
          v-for="(product, i) in filteredProducts"
          :key="i"
          @click.stop.prevent="onProductClicked(i)"
        >
          <OrderProduct
            :product="product"
            :isOrderFinished="isDone"
            :isSelected="isProductSelected(i)"
            :productKey="`${order.id}prod${i}`"
          />
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { OrderProductStatus } from "@/model/order";
import OrderProduct from "./OrderProduct";
import Avatars from "@/views/dashboard/components/Avatars";

export default {
  props: {
    order: {},
    currentTime: Date,
    filterStatus: String,
    selectedProducts: Array
  },
  components: { OrderProduct, Avatars },
  data() {
    return {
      colors: {
        deliveredAndPaid: "#999999",
        tooLong: "#d15e5e",
        caution: "#e6a947",
        ok: "#6bb377"
      }
    };
  },

  computed: {
    isDelivered() {
      return this.filterStatus === OrderProductStatus.delivered;
    },

    isDone() {
      return (
        this.order.hasBeenPaid &&
        this.order.products.every(
          product => product.status === OrderProductStatus.delivered
        )
      );
    },

    isReady() {
      return (
        this.filterStatus === OrderProductStatus.delivered ||
        this.order.products.every(
          product =>
            product.status === OrderProductStatus.ready ||
            product.status !== OrderProductStatus.preparing
        )
      );
    },

    filteredProducts() {
      return this.order.products.filter(product => {
        if (!this.filterStatus) {
          return true;
        }
        return product.status === this.filterStatus;
      });
    },

    time() {
      let time = this.orderTime();
      let timeString =
        !time.hours && !time.min
          ? "< 1min"
          : `${time.hours ? time.hours + "h" : ""} ${time.min}min`;
      // TODO Update colors to Beyla colors for error, warning, success
      let color = this.orderColor();
      return { waited: timeString, color: color };
    },

    location() {
      return this.$store.getters.locations.find(
        location => location.id === this.order.location
      );
    },

    showCheckbox() {
      return this.filterStatus === OrderProductStatus.preparing;
    }
  },

  methods: {
    isProductSelected(index) {
      return (
        this.selectedProducts &&
        this.selectedProducts.some(selected => selected === index)
      );
    },

    maybeTogglePayment() {
      this.showPaymentDialog(
        this.order.hasBeenPaid ? "cancelPayment" : "confirmPayment"
      );
    },

    toggleReady() {
      if (this.isReady) {
        this.emitMoveOrder(
          OrderProductStatus.ready,
          OrderProductStatus.preparing
        );
      } else {
        this.emitMoveOrder(
          OrderProductStatus.preparing,
          OrderProductStatus.ready
        );
      }
    },

    onSwipe(direction) {
      if (direction === "left" || direction === "right") {
        this.justSwiped = true;
        let isNext = direction === "right";
        this.moveOrder(isNext);
      }
    },

    moveOrder(isNext, selectedWrappers) {
      let from = null;

      if (selectedWrappers) {
        from = selectedWrappers[0].product.status;
      } else {
        // Calculate status
        from =
          this.filterStatus !== OrderProductStatus.preparing
            ? this.filterStatus
            : this.isReady
            ? OrderProductStatus.ready
            : OrderProductStatus.preparing;
      }

      let to = OrderProductStatus[isNext ? "next" : "previous"](from);

      if (to) {
        if (
          this.order.hasBeenPaid &&
          this.filterStatus === OrderProductStatus.delivered
        ) {
          this.showPaymentDialog("cancelPayment");
        } else {
          this.order.updateProductsStatus(from, to);
        }
      } else if (!this.order.hasBeenPaid) {
        this.showPaymentDialog("confirmPayment");
      }
    },

    handlePending(isNext, selectedWrappers) {
      if (isNext) {
        this.emitMoveOrder(
          this.filterStatus,
          OrderProductStatus.preparing,
          selectedWrappers
        );
      }
    },

    handlePreparing(isNext, selectedWrappers, fromReady) {
      let to = null;
      if (isNext) {
        to = fromReady
          ? OrderProductStatus.delivered
          : OrderProductStatus.ready;
      } else {
        to = fromReady
          ? OrderProductStatus.preparing
          : OrderProductStatus.pending;
      }
      let from = fromReady
        ? OrderProductStatus.ready
        : OrderProductStatus.preparing;
      this.emitMoveOrder(from, to, selectedWrappers);
    },

    handleDelivered(isNext, selectedWrappers) {
      if (!this.order.hasBeenPaid) {
        if (isNext) {
          this.showPaymentDialog("confirmPayment");
        } else {
          this.emitMoveOrder(
            this.filterStatus,
            OrderProductStatus.ready,
            selectedWrappers
          );
        }
      } else {
        if (!isNext) {
          this.showPaymentDialog("cancelPayment");
        }
      }
    },

    emitMoveOrder(from, to, selectedWrappers) {
      this.$emit("moveOrder", from, to, selectedWrappers);
    },

    showPaymentDialog(content) {
      this.$emit("openPaymentDialog", content, this.order);
    },

    onOrderClicked() {
      this.$emit("filter", { order: this.order.id });
      //("onOrderClicked");
    },

    onUserClicked() {
      this.$emit("filter", { user: this.order.userId });
      //alert("onUserClicked");
    },

    onLocationClicked() {
      this.$emit("filter", { location: this.order.location });
      // alert("onLocationClicked");
    },

    onProductClicked(index) {
      this.$emit("productSelected", this.order.id, index);
    },

    areAllProductsFinished() {
      return this.order.products.every(
        product =>
          product.status == OrderProductStatus.delivered &&
          this.order.hasBeenPaid
      );
    },

    orderTime() {
      let ms = new Date() - this.order.date;
      let min = Math.floor(ms / 1000 / 60);
      let hours = Math.floor(min / 60);
      return { hours: hours, min: min % 60 };
    },

    orderColor() {
      let time = this.orderTime();
      // TODO Give it another thought

      if (
        this.order.hasBeenPaid &&
        this.filterStatus === OrderProductStatus.delivered
      ) {
        return "#999999";
      }

      return time.hours
        ? "#d15e5e"
        : !time.min || time.min < 15
        ? "#6bb377"
        : time.min < 30
        ? "#e6a947"
        : "#d15e5e";
    }
  }
};
</script>

<style></style>
